import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import ReactSelect from "react-select";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import { Accordion } from "components/Accordian";
import { ContentCard } from "components/ContentCard";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import CenterLoading from "components/common/CenterLoading";
import CustomModal from "components/Modals/CustomModal";
import rulesApi from "services/apis/rulesApi";
import { nameCapitalize, getPathPrefix } from "utils/coreUtils";
import "../styles/DataStreamReport.scss";

function DataStreamReport() {
  const [isLoading, setIsLoading] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [viewReportJson, setViewReportJson] = useState("");
  const [jsonToView, setJsonToView] = useState();
  const [customReportData, setCustomReportData] = useState([]);
  const [t] = useTranslation();
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDateOperator, setSelectedDateOperator] = useState("");
  const [date] = useState([
    {
      label: t("processingScreen.dateEqual"),
      value: "equal",
    },
    {
      label: t("processingScreen.dateGreater"),
      value: "greater",
    },
    {
      label: t("processingScreen.dateLess"),
      value: "less",
    },
    {
      label: t("processingScreen.dateIsBetween"),
      value: "between",
    },
  ]);
  const streamObj = useSelector((state) => state?.datastream?.selected);

  const ruleResponse = async () => {
    try {
      setIsLoading(true);
      const response = await rulesApi.getResultReportByDSConfigId({
        dsConfigId: streamObj.dataStreamConfigId,
      });

      setCustomReportData(response?.data);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    ruleResponse();
  }, []);
  const showViewJsonModal = async (streamObj) => {
    try {
      const jsonContent = await rulesApi.getJSONFile(streamObj?.jsonFileUrl);
      setJsonToView(JSON.stringify(jsonContent.data?.jsonOutput, null, 4));
    } catch (error) {
      console.log(error);
    }
  };
  const columns = useMemo(
    () => [
      {
        name: nameCapitalize(t("processingScreen.columns.report")),
        selector: (row) => {
          return (
            <div>
              <OverlayTrigger
                placement="auto"
                overlay={(props) => (
                  <Tooltip id="button-tooltip" {...props}>
                    {nameCapitalize(row?.report)}
                  </Tooltip>
                )}
              >
                <a className="report-name-text active">
                  <div
                    onClick={() => {
                      setViewReportJson(row.report);
                      showViewJsonModal(row);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {row?.report}
                  </div>
                </a>
              </OverlayTrigger>
            </div>
          );
        },
        minWidth: "100px",
        className: "",
      },
      {
        name: t("processingScreen.columns.version"),
        selector: (row) => row?.version,
        minWidth: "180px",
        center: true,
      },
      {
        name: t("processingScreen.columns.status"),
        selector: (row) => row?.status,
        minWidth: "180px",
        center: true,
      },
      {
        name: t("processingScreen.columns.processedRule"),
        selector: (row) => row?.processedRules,
        minWidth: "180px",
        center: true,
      },
      {
        name: t("processingScreen.columns.date"),
        selector: (row) => row?.createdAt,

        minWidth: "180px",
        center: true,
      },
    ],
    []
  );

  const searchReport = useMemo(() => {
    return (
      <>
        <div className="form-group col" style={{ paddingRight: "8px" }}>
          <input
            className="form-control"
            placeholder={t("processingScreen.searchReportplaceName")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (e.target.value === "") {
                setResetPaginationToggle(!resetPaginationToggle);
              }
            }}
          />
        </div>
        <div className="form-group col justify-content-end p-2">
          <ReactSelect
            classNamePrefix="select"
            options={date}
            value={date.find((item) => item.value === selectedDateOperator)}
            placeholder={t("processingScreen.dateDropPlaceholder")}
            menuPosition="fixed"
            onChange={(event) => {
              setSelectedDateOperator(event.value);
            }}
          />
        </div>
        {["greater", "between", "less", "equal"].includes(
          selectedDateOperator
        ) && (
          <div className="form-group col justify-content-end p-2">
            <input
              className="form-control"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (e.target.value === "") {
                  setResetPaginationToggle(!resetPaginationToggle);
                }
              }}
            />
          </div>
        )}
        {selectedDateOperator === "between" && (
          <div className="form-group col justify-content-end p-2">
            <input
              className="form-control"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (e.target.value === "") {
                  setResetPaginationToggle(!resetPaginationToggle);
                }
              }}
            />
          </div>
        )}

        <div className="col d-flex justify-content-end align-item-end mb-3 clear-btn-pad">
          <PrimaryButton
            type="reset"
            icon="fa-solid fa-arrows-rotate"
            title={t("processingScreen.refresh")}
            className="ms-3"
            onClick={() => {
              setSearch("");
              setStartDate("");
              setEndDate("");
              setSelectedDateOperator("");
              ruleResponse();
            }}
          />
        </div>
      </>
    );
  }, [search, resetPaginationToggle, startDate, selectedDateOperator, endDate]);
  const filteredReports = customReportData.filter((item) => {
    const date = moment(item.createdAt);

    const isGreater =
      selectedDateOperator === "greater"
        ? startDate
          ? date.isSameOrBefore(moment(startDate), "date")
          : true
        : true;

    const isLesser =
      selectedDateOperator === "less"
        ? startDate
          ? date.isSameOrAfter(moment(startDate), "date")
          : true
        : true;

    const isBetween =
      selectedDateOperator === "between"
        ? startDate && endDate
          ? date.isSameOrAfter(moment(startDate), "date") &&
            date.isSameOrBefore(moment(endDate), "date")
          : true
        : true;
    const isEqual =
      selectedDateOperator === "equal"
        ? startDate
          ? date.isSame(moment(startDate), "day")
          : true
        : true;

    return (
      isGreater &&
      isLesser &&
      isBetween &&
      isEqual &&
      ((item.report &&
        item.report.toLowerCase().includes(search.toLowerCase())) ||
        (item.status &&
          item.status.toLowerCase().includes(search.toLowerCase())))
    );
  });

  //  Custom Style for Checkbox
  const Checkbox = React.forwardRef(({ onClick, ...rest }, ref) => {
    return (
      <>
        <div className="form-check form-check-sm form-check-custom">
          <input
            type="checkbox"
            className="form-check-input"
            ref={ref}
            onClick={onClick}
            {...rest}
          />
        </div>
      </>
    );
  });
  Checkbox.displayName = "Checkbox";
  const customStyles = {
    title: {
      style: {
        color: "#004e5c !important",
        fontWeight: "800",
      },
    },
    rows: {
      style: {
        borderBottom: "0px !important",
      },
    },
    headCells: {
      style: {
        fontSize: "14px",
        fontWeight: "800",
        color: "#004e5c !important",
        borderBottom: "0px !important",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
      },
    },
  };
  const autoMessage = jsonToView
    ? JSON.parse(jsonToView)?.RuleEngineValidationResult?.SummaryResult
        ?.AutoMessage
    : "";
  return (
    <>
      <CustomModal
        title={t("processingScreen.columns.report")}
        show={!!viewReportJson}
        size={"lg"}
        body={
          <div>
            <div
              className="pop-up-msg"
              style={{ maxHeight: "50vh", overflow: "auto" }}
            >
              <pre style={{ backgroundColor: "#ecebfb" }}>{jsonToView}</pre>
            </div>
            {autoMessage && (
              <Accordion title={t("processingScreen.autoReplyTitle")}>
                {<div dangerouslySetInnerHTML={{ __html: autoMessage }} />}
              </Accordion>
            )}
          </div>
        }
        centered
        customButtons={
          <div className="row">
            <div className="col-auto">
              <PrimaryButton
                title={t("CommonKeys.okButtonTitle")}
                onClick={() => setViewReportJson("")}
              />
            </div>
            <div className="col-auto">
              <PrimaryButton
                title={t("CommonKeys.download")}
                onClick={() => {
                  const blob = new Blob([jsonToView], {
                    type: "application/json",
                  });
                  const url = URL.createObjectURL(blob);

                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${viewReportJson}.json`;
                  a.click();
                  setViewReportJson("");
                  URL.revokeObjectURL(url); // clean up
                }}
              />
            </div>
          </div>
        }
      />
      <AiccBreadCrumb
        homeLink={getPathPrefix("dashboard")}
        title={streamObj?.customDataStreamName}
        items={[
          { name: t("processingScreen.ruleEngine") },
          { name: t("processingScreen.dataStreamReport") },
        ]}
      />
      <ContentCard
        headerImgSrc="/assets/images/report-icon.png"
        title={`${t("processingScreen.dataStreamReport")} - ${streamObj?.customDataStreamName}`}
        bodyContent={
          <div className="tab-content" id="v-pills-tabContent">
            <div
              className="tab-pane fade show active"
              id="work"
              role="tabpanel"
              aria-labelledby="work-tab"
            >
              <label className="mb-2 form-label" style={{ fontSize: "16px" }}>
                {t("processingScreen.searchReportLabel")}
              </label>
              <div className="table-responsive dataStream-report">
                <DataTable
                  responsive
                  columns={columns}
                  data={filteredReports}
                  noDataComponent={t("processingScreen.columns.noData")}
                  progressPending={isLoading}
                  progressComponent={<CenterLoading minHeight={"150px"} />}
                  paginationResetDefaultPage={resetPaginationToggle}
                  pagination
                  selectableRows
                  striped
                  selectableRowsSingle
                  selectableRowsComponent={Checkbox}
                  customStyles={customStyles}
                  subHeader
                  subHeaderAlign="left"
                  subHeaderWrap={true}
                  subHeaderComponent={searchReport}
                />
              </div>
            </div>
          </div>
        }
      />
    </>
  );
}

export default DataStreamReport;
